import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import {
    MdPointOfSale,
    MdReplay,
    MdReceiptLong,
    MdAssignmentReturn,
    MdRequestQuote,
    MdSearch,
    MdToday,
    MdDateRange,
    MdManageAccounts,
    MdExitToApp,
} from 'react-icons/md';

const SIDEBAR_FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function Sidebar() {
    const navigate = useNavigate();
    const { logout, isAdmin } = useAuth();
    const theme = useTheme();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const linkStyle = (isActive) => ({
        display: 'flex',
        alignItems: 'center',
        padding: '0.75rem 1rem',
        marginBottom: '0.35rem',
        color: isActive ? '#ffffff' : '#475569',
        textDecoration: 'none',
        borderRadius: '8px',
        fontWeight: isActive ? '600' : '500',
        backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
    });

    const iconStyle = {
        marginRight: '10px',
        fontSize: '1.3rem',
    };

    return (
        <nav
            className="sidebar-nav"
            style={{
                width: '220px',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontFamily: SIDEBAR_FONT,
                display: 'flex',
                flexDirection: 'column',
                // No padding here - it's split between the two zones below so
                // the divider line (borderTop on the footer) reaches full width.
                // No hardcoded height/top: this fills its flex parent, which
                // App.js already constrains to exactly the space below the
                // Topbar, whatever that actually renders as.
                height: '100%',
                flexShrink: 0,
                boxShadow: '2px 0 8px rgba(0, 0, 0, 0.06)',
                borderRight: '1px solid #eef0f4',
                zIndex: 999,
            }}
        >
            {/* NavLink styles are set inline (see linkStyle), but :hover can't be
                expressed inline - this covers just the hover state for inactive
                items without needing an onMouseEnter/Leave handler per link. */}
            <style>{`
                .sidebar-nav a:not(.active):hover {
                    background-color: #f1f5f9;
                }
            `}</style>

            {/* Scrollable zone: only this section scrolls if the nav links
                ever don't fit, so logout below is never pushed off-screen
                or hidden behind a scrollbar. */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 1rem' }}>
                <NavLink to="/cash-sale" style={({ isActive }) => linkStyle(isActive)}>
                    <MdPointOfSale style={iconStyle} />
                    Cash Sale
                </NavLink>
                <NavLink to="/refund" style={({ isActive }) => linkStyle(isActive)}>
                    <MdReplay style={iconStyle} />
                    Refund
                </NavLink>
                <NavLink to="/account-invoice" style={({ isActive }) => linkStyle(isActive)}>
                    <MdReceiptLong style={iconStyle} />
                    Account Invoice
                </NavLink>
                <NavLink to="/account-credit-note" style={({ isActive }) => linkStyle(isActive)}>
                    <MdAssignmentReturn style={iconStyle} />
                    Account Credit Note
                </NavLink>
                {/* Visible to every logged-in user, unlike Monthly Report/Manage
                    Accounts below - quoting isn't an admin-only capability. */}
                <NavLink to="/quotes" style={({ isActive }) => linkStyle(isActive)}>
                    <MdRequestQuote style={iconStyle} />
                    Quotes
                </NavLink>
                <NavLink to="/search" style={({ isActive }) => linkStyle(isActive)}>
                    <MdSearch style={iconStyle} />
                    Search
                </NavLink>
                <NavLink to="/daily-report" style={({ isActive }) => linkStyle(isActive)}>
                    <MdToday style={iconStyle} />
                    Daily Report
                </NavLink>
                {/* Monthly Report and Manage Accounts were admin-only in the
                    old Angular app - hidden here to match, with AdminRoute
                    in App.js as the actual enforcement (this alone wouldn't
                    stop someone typing the URL directly). */}
                {isAdmin && (
                    <NavLink to="/monthly-report" style={({ isActive }) => linkStyle(isActive)}>
                        <MdDateRange style={iconStyle} />
                        Monthly Report
                    </NavLink>
                )}
                {isAdmin && (
                    <NavLink to="/manage-accounts" style={({ isActive }) => linkStyle(isActive)}>
                        <MdManageAccounts style={iconStyle} />
                        Manage Accounts
                    </NavLink>
                )}
            </div>

            {/* Fixed footer zone: never part of the scrolling area above, so
                it's always visible at the bottom of the sidebar. */}
            <div style={{ flexShrink: 0, padding: '1rem', borderTop: '1px solid #eef0f4' }}>
                <button
                    onClick={handleLogout}
                    style={{
                        width: '100%',
                        backgroundColor: '#475569',
                        border: 'none',
                        color: 'white',
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontFamily: SIDEBAR_FONT,
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#334155')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#475569')}
                >
                    <MdExitToApp style={{ marginRight: '10px', fontSize: '1.3rem' }} />
                    Logout
                </button>
            </div>
        </nav>
    );
}

export default Sidebar;
